import React, { useEffect, useState } from "react";
import io from "socket.io-client";
import ChatList from "../components/ChatList";
import ChatWindow from "../components/ChatWindow";
import api from "../services/api";
import { toast } from "react-toastify";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Paper,
  Avatar,
  IconButton,
  Button,
  Badge,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

const socket = io(process.env.REACT_APP_API, { transports: ["websocket"] });

export default function Chat() {
  const [chats, setChats] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [tab, setTab] = useState(0);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    pedido: null,
    novoStatus: "",
    avisar: true,
  });

  const statusTabs = ["andamento", "producao", "retirada", "delivery", "finalizado"];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // controla qual painel aparece no mobile: 'list' (conversas) | 'center' (pedidos/chat)
  const [mobilePanel, setMobilePanel] = useState(isMobile ? "list" : "center");

  useEffect(() => {
    // quando mudar between mobile/desktop, ajusta painel inicial
    setMobilePanel(isMobile ? "list" : "center");
  }, [isMobile]);

  const abrirChat = (chat) => {
    if (!chat) return;

    socket.emit("joinChat", chat.id);

    setSelectedChat({ ...chat });
    setMensagens([]);

    // 🔄 recarregar lista
    api.get("/chats").then((res) => setChats(res.data));
    if (isMobile) setMobilePanel("center");
  };

  // 🔹 Carrega chats e pedidos
  useEffect(() => {
    async function carregarDados() {
      try {
        const [chatsRes, pedidosRes] = await Promise.all([
          api.get("/chats"),
          api.get("/pedidos"),
        ]);
        setChats(chatsRes.data);
        setPedidos(pedidosRes.data);
      } catch (err) {
        console.error("❌ Erro ao carregar dados:", err);
        toast.error("Erro ao carregar conversas e pedidos");
      }
    }
    carregarDados();
  }, []);

  // 🔹 Registro global para qualquer lugar abrir chat automaticamente
  useEffect(() => {
    window.onChatCreatedGlobal = (chat) => {
      abrirChat(chat);

      // 🔥 garante que aparece na lista lateral
      setChats((prev) => {
        if (prev.some((c) => c.id === chat.id)) return prev;
        return [chat, ...prev];
      });
    };

    return () => {
      window.onChatCreatedGlobal = null;
    };
  }, []);

  // 🔹 WebSocket: sincronização em tempo real
  useEffect(() => {
    socket.on("connect", () =>
      console.log("⚡ Socket conectado:", socket.id)
    );

    socket.on("novoChat", (chat) => {
      setChats((prev) => {
        if (prev.some((c) => c.id === chat.id)) return prev;
        return [chat, ...prev];
      });
    });

    socket.on("chatAtualizado", (chat) =>
      setChats((prev) => prev.map((c) => (c.id === chat.id ? chat : c)))
    );

    socket.on("novoPedido", (pedido) => {
      setPedidos((prev) => {
        if (prev.some((p) => p.id === pedido.id)) return prev;
        return [pedido, ...prev];
      });
    });

    socket.on("pedidoAtualizado", (pedidoAtualizado) => {
      setPedidos((prev) =>
        prev.map((p) => (p.id === pedidoAtualizado.id ? pedidoAtualizado : p))
      );

      if (
        selectedChat &&
        selectedChat.id === pedidoAtualizado.chatId &&
        pedidoAtualizado.status === "finalizado"
      ) {
        setSelectedChat(null);
        toast.info(`✅ Pedido #${pedidoAtualizado.id} finalizado!`);
      }
    });

    socket.on("pedidoRemovido", (id) =>
      setPedidos((prev) => prev.filter((p) => p.id !== id))
    );

    socket.on("chatFinalizado", (id) => {
      setChats((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status: "finalizado" } : c))
      );
      setSelectedChat(null);
    });

    socket.on("novaMensagem", (payload) => {
      const msg = payload?.mensagem ?? payload;
      const chatId = payload?.chatId ?? msg?.chatId;
      if (!msg || !selectedChat?.id || chatId !== selectedChat.id) return;

      const uniqueKey = [
        msg.id,
        msg.texto || msg.body || "",
        msg.tipo || "",
        msg.midiaUrl || "",
        msg.direcao || "",
      ].join("|");

      setMensagens((prev) => {
        const jaExiste = prev.some((m) => {
          const key = [
            m.id,
            m.texto || m.body || "",
            m.tipo || "",
            m.midiaUrl || "",
            m.direcao || "",
          ].join("|");
          return key === uniqueKey;
        });

        const localMatch = prev.find(
          (m) =>
            m.local === true &&
            m.tipo === msg.tipo &&
            (m.texto === msg.texto || m.midiaUrl === msg.midiaUrl)
        );

        if (localMatch)
          return prev.map((m) =>
            m.id === localMatch.id ? { ...msg, local: false } : m
          );

        return jaExiste ? prev : [...prev, msg];
      });
    });

    return () => {
      socket.removeAllListeners();
    };
  }, [selectedChat]);

  // 🔹 Carrega mensagens do chat selecionado
  useEffect(() => {
    if (!selectedChat?.id) {
      setMensagens([]);
      return;
    }

    async function carregarMensagens() {
      try {
        // limpa imediatamente para não mostrar chat anterior
        setMensagens([]);
        const { data } = await api.get(`/chats/${selectedChat.id}/mensagens`);
        setMensagens(data || []);
      } catch (err) {
        console.error("❌ Erro ao carregar mensagens:", err);
        setMensagens([]);
      }
    }

    carregarMensagens();
  }, [selectedChat?.id]);

  // 🔹 Envio de mensagem
  const handleSend = ({ Phone, Body }) => {
    if (!Body?.trim() || !selectedChat) return;

    const phoneValue =
      Phone ||
      selectedChat?.telefone?.toString().replace(/\D/g, "") ||
      selectedChat?.numero?.toString().replace(/\D/g, "");

    const agora = new Date().toISOString();
    const localId = `${selectedChat.id}-${Date.now()}`;

    const novaMsg = {
      chatId: selectedChat.id,
      Phone: phoneValue,
      Body: Body.trim(),
      direcao: "out",
      localId,
    };

    socket.emit("mensagem", novaMsg);

    setMensagens((prev) => [
      ...prev,
      {
        ...novaMsg,
        texto: Body.trim(),
        tipo: "text",
        remetente: "atendente",
        dataHora: agora,
        createdAt: agora,
        updatedAt: agora,
      },
    ]);
  };

  // // 🔧 Função utilitária para converter arquivo em base64
  // const toBase64 = (file) =>
  //   new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.onload = () => resolve(reader.result);
  //     reader.onerror = reject;
  //     reader.readAsDataURL(file);
  //   });

  // // 🔹 Enviar imagem
  // const handleSendImage = async (file) => {
  //   if (!file || !selectedChat) return;

  //   try {
  //     const imageBase64 = await toBase64(file);
  //     await api.post("/chat/send/image", {
  //       number: selectedChat.telefone,
  //       image: imageBase64,
  //       caption: file.name,
  //       nome: selectedChat.nome,
  //     });

  //     toast.success("🖼️ Imagem enviada!");
  //   } catch (err) {
  //     console.error("❌ Erro ao enviar imagem:", err);
  //     toast.error("Erro ao enviar imagem");
  //   }
  // };

  // // 🔹 Enviar áudio
  // const handleSendAudio = async (file) => {
  //   if (!file || !selectedChat) return;

  //   try {
  //     const audioBase64 = await toBase64(file);
  //     await api.post("/chat/send/audio", {
  //       number: selectedChat.telefone,
  //       audio: audioBase64,
  //       ptt: true,
  //       nome: selectedChat.nome,
  //     });

  //     toast.success("🎧 Áudio enviado!");
  //   } catch (err) {
  //     console.error("❌ Erro ao enviar áudio:", err);
  //     toast.error("Erro ao enviar áudio");
  //   }
  // };

  // // 🔹 Enviar documento
  // const handleSendDocument = async (file) => {
  //   if (!file || !selectedChat) return;

  //   try {
  //     const docBase64 = await toBase64(file);
  //     await api.post("/chat/send/document", {
  //       number: selectedChat.telefone,
  //       document: docBase64,
  //       filename: file.name,
  //       caption: file.name,
  //       nome: selectedChat.nome,
  //     });

  //     toast.success("📄 Documento enviado!");
  //   } catch (err) {
  //     console.error("❌ Erro ao enviar documento:", err);
  //     toast.error("Erro ao enviar documento");
  //   }
  // };

  // 🔹 Helper para detectar se é entrega ou retirada
  const isEntrega = (pedido) => {
    const resumo = pedido?.resumo?.toLowerCase() || "";
    return resumo.includes("🚚 *entrega*") || resumo.includes("🏠 endereço:");
  };

  // 🔹 Filtro de pedidos por aba
  const statusAtual = statusTabs[tab];
  const pedidosFiltrados = pedidos
    .filter((p) => !p.fake)
    .filter((p) => {
      if (statusAtual === "retirada") return p.status === "delivery" && !isEntrega(p);
      if (statusAtual === "delivery") return p.status === "delivery" && isEntrega(p);
      return p.status === statusAtual;
    })
    .sort((a, b) => {
      // Ordena por dataHora crescente (mais antigo primeiro)
      const da = new Date(a.dataHora).getTime();
      const db = new Date(b.dataHora).getTime();
      return da - db;
    });

  const handleMudarStatus = (pedido) => {
    const novoStatus =
      pedido.status === "andamento"
        ? "producao"
        : pedido.status === "producao"
        ? "delivery"
        : "finalizado";

    // 👇 Agora só abre o modal
    setConfirmDialog({
      open: true,
      pedido,
      novoStatus,
      avisar: true,
    });
  };

  const confirmarMudancaStatus = async () => {
    const { pedido, novoStatus, avisar } = confirmDialog;

    try {
      await api.put(`/pedidos/${pedido.id}/status`, { status: novoStatus });

      if (avisar) {
        // 🔹 Detecta se é retirada ou entrega
        const ehRetirada = !isEntrega(pedido);
        
        const mensagens = {
          producao: "🍳 Seu pedido agora está em PRODUÇÃO!",
          delivery: ehRetirada 
            ? "📍 Seu pedido está PRONTO PARA RETIRADA!" 
            : "🚚 Seu pedido saiu para ENTREGA!",
          finalizado: "✅ Seu pedido foi FINALIZADO! Obrigado pela preferência!",
        };

        const texto = mensagens[novoStatus];

        if (texto) {
          const chat = chats.find(
            (c) =>
              c.telefone?.toString().replace(/\D/g, "") ===
              pedido.telefone?.toString().replace(/\D/g, "")
          );

          if (!chat) {
            toast.error("Chat do cliente não foi encontrado.");
            return;
          }

          console.log(chat);
          await api.post(
            "/chat/send/text",
            {
              Phone: pedido.telefone,
              Body: texto,
              chatId: chat.id,
              nome: pedido.nome,
            },
            {
              headers: { "Content-Type": "application/json; charset=utf-8" },
              transformRequest: [
                (data) => new TextEncoder().encode(JSON.stringify(data)),
              ],
            }
          );

          toast.success("Mensagem enviada ao cliente!");
        }
      }

      // fechar modal
      setConfirmDialog({ open: false, pedido: null, novoStatus: "", avisar: true });
    } catch (err) {
      console.error(err);
      toast.error("Erro ao atualizar status");
    }
    // Agora sim fecha corretamente
    setConfirmDialog({
      open: false,
      pedido: null,
      novoStatus: "",
      avisar: true,
    });
  };

  const handleExcluir = async (pedido) => {
    if (!window.confirm(`Deseja realmente excluir o pedido #${pedido.id}?`))
      return;
    try {
      await api.delete(`/pedidos/${pedido.id}`);
      toast.success(`Pedido #${pedido.id} excluído com sucesso!`);
      setPedidos((prev) => prev.filter((p) => p.id !== pedido.id));
    } catch {
      toast.error("Erro ao excluir pedido");
    }
  };

  const handleImprimir = async (pedido) => {
    try {
      const cabecalho = localStorage.getItem("printCabecalho") || "";
      const rodape = localStorage.getItem("printRodape") || "";
      
      const { data } = await api.post(`/pedidos/${pedido.id}/imprimir`, {
        cabecalho,
        rodape
      });
      
      toast.success(data.message || `🖨️ Pedido #${pedido.id} impresso com sucesso!`);
    } catch {
      toast.error("Erro ao imprimir pedido");
    }
  };

  const contagem = {
    andamento: pedidos.filter((p) => p.status === "andamento" && !p.fake).length,
    producao: pedidos.filter((p) => p.status === "producao" && !p.fake).length,
    retirada: pedidos.filter((p) => p.status === "delivery" && !p.fake && !isEntrega(p)).length,
    delivery: pedidos.filter((p) => p.status === "delivery" && !p.fake && isEntrega(p)).length,
    finalizado: pedidos.filter((p) => p.status === "finalizado" && !p.fake).length,
  };

  const getResumoBg = (status) => {
    switch (status) {
      case "andamento":
        return "#fffbea";
      case "producao":
        return "#f1f5f9";
      case "delivery":
        return "#ecfdf5";
      default:
        return "#f9fafb";
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: isMobile ? 56 : 64,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        overflow: "hidden",
        background: "#f9fafb",
      }}
    >
      {/* Mobile top bar: single area for back + tabs.
          - shows back icon when in 'center' (pedidos or chat) so user can go back to list
          - shows tabs when not viewing a chat (selectedChat === null)
          - when viewing a chat on mobile shows only back arrow (no tabs) */}
      {isMobile && (
        <Box
          position="sticky"
          top={0}
          zIndex={20}
          bgcolor="#fff"
          borderBottom="1px solid #e5e7eb"
          p={0.5}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: 64,
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          {/* voltar para o chat list quando estiver no painel central (tabs) */}
          {isMobile && mobilePanel === "center" && !selectedChat && (
            <IconButton
              size="small"
              onClick={() => setMobilePanel("list")}
              style={{ marginRight: 4 }} // menor espaçamento para ficar mais próximo da primeira tab
              title="Voltar para lista"
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          {/* Se estiver dentro de uma conversa móvel - mostrar seta + nome do contato */}
          {selectedChat && (
            <Box display="flex" alignItems="center" style={{ width: "100%", gap: 10,paddingTop:15 }}>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedChat(null);
                  setMobilePanel("list");
                }}
              >
                <ArrowBackIcon />
              </IconButton>

              <Avatar
                style={{
                  backgroundColor: "#2b6cb0",
                  color: "#fff",
                  width: 40,
                  height: 40,
                  fontWeight: 700,
                }}
              >
                {(selectedChat?.nome || selectedChat?.title || selectedChat?.telefone || "U")
                  .toString()
                  .trim()
                  .charAt(0)
                  .toUpperCase()}
              </Avatar>

              <Box style={{ overflow: "hidden", minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    lineHeight: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedChat?.nome || selectedChat?.title || "Conversa"}
                </Typography>
                <Typography
                  variant="caption"
                  style={{
                    color: "#6b7280",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                  }}
                >
                  {selectedChat?.telefone || selectedChat?.numero || ""}
                </Typography>
              </Box>
            </Box>
          )}

           {/* only show the tabs when not inside a chat (compact) */}
          {!selectedChat && (
            <Tabs
              value={tab}
              onChange={(e, v) => {
                setTab(v);
                setSelectedChat(null);
                setMobilePanel("center");
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              style={{
                flex: 1,
                marginLeft: 4,
                marginRight: 4,
                minHeight: 52,
                borderRadius: 20,
                border: "1px solid #e5e7eb",
                padding: 4,
                boxSizing: "border-box",
                backgroundColor: "#edf1f5",
              }}
              TabIndicatorProps={{ style: { height: 3, borderRadius: 3 } }}
            >
              <Tab
                style={{
                  minHeight: 44,
                  padding: "6px 10px",
                  borderRight: "1px solid #e5e7eb",
                }}
                label={
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <span style={{ fontSize: 18 }}>🛒</span>
                    <span style={{ fontWeight: 700, fontSize: 12, color: "#1f2937" }}>Pedido</span>
                    <Badge
                      color="primary"
                      badgeContent={contagem.andamento}
                      style={{ pointerEvents: "none", marginLeft: 8, transform: "translateY(-2px)" }}
                    />
                  </Box>
                }
              />

              <Tab
                style={{
                  minHeight: 44,
                  padding: "6px 10px",
                  borderRight: "1px solid #e5e7eb",
                }}
                label={
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <span style={{ fontSize: 18 }}>🍽️</span>
                    <span style={{ fontWeight: 700, fontSize: 12, color: "#1f2937" }}>Cozinha</span>
                    <Badge
                      color="secondary"
                      badgeContent={contagem.producao}
                      style={{ pointerEvents: "none", marginLeft: 8, transform: "translateY(-2px)" }}
                    />
                  </Box>
                }
              />

              <Tab
                style={{
                  minHeight: 44,
                  padding: "6px 10px",
                  borderRight: "1px solid #e5e7eb",
                }}
                label={
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <span style={{ fontSize: 18 }}>📍</span>
                    <span style={{ fontWeight: 700, fontSize: 12, color: "#1f2937" }}>Retirada</span>
                    <Badge
                      color="error"
                      badgeContent={contagem.retirada}
                      style={{ pointerEvents: "none", marginLeft: 8, transform: "translateY(-2px)" }}
                    />
                  </Box>
                }
              />

              <Tab
                style={{
                  minHeight: 44,
                  padding: "6px 10px",
                  borderRight: "1px solid #e5e7eb",
                }}
                label={
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <span style={{ fontSize: 18 }}>🚚</span>
                    <span style={{ fontWeight: 700, fontSize: 12, color: "#1f2937" }}>Delivery</span>
                    <Badge
                      color="error"
                      badgeContent={contagem.delivery}
                      style={{ pointerEvents: "none", marginLeft: 8, transform: "translateY(-2px)" }}
                    />
                  </Box>
                }
              />

              {/* última Tab sem borda direita */}
              <Tab
                style={{
                  minHeight: 44,
                  padding: "6px 10px",
                }}
                label={
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <span style={{ fontSize: 18 }}>✅</span>
                    <span style={{ fontWeight: 700, fontSize: 12, color: "#1f2937" }}>Finalizados</span>
                    <Badge
                      color="default"
                      badgeContent={contagem.finalizado}
                      style={{ pointerEvents: "none", marginLeft: 8, transform: "translateY(-2px)" }}
                    />
                  </Box>
                }
              />
            </Tabs>
          )}
        </Box>
      )}

      {/* ChatList: desktop sempre | mobile quando mobilePanel === 'list' */}
      {(!isMobile || mobilePanel === "list") && (
        <div
          style={{
            width: isMobile ? "100%" : 360,
            flex: isMobile ? "0 0 auto" : "0 0 360px",
            borderRight: !isMobile ? "1px solid #e5e7eb" : "none",
            overflowY: "auto",
            background: "#fff",
          }}
        >
          <ChatList
            chats={chats}
            selectedId={selectedChat?.id}
            onSelect={(chat) => {
              setSelectedChat(chat ? { ...chat } : null);
              setMensagens([]); // limpa mensagens do chat anterior
              if (isMobile) setMobilePanel("center");
            }}
          />
        </div>
      )}

      {/* Centro (pedidos / chat) - desktop sempre | mobile quando mobilePanel === 'center' */}
      {(!isMobile || mobilePanel === "center") && (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            borderLeft: !isMobile ? "1px solid #e5e7eb" : "none",
            borderTop: isMobile ? "1px solid #e5e7eb" : "none",
            overflow: "hidden",
          }}
        >
          {/* Cabeçalho - já contém back button; reduz espaçamento em mobile */}
          <Box
            display="flex"
            alignItems="center"
            borderBottom="1px solid #e5e7eb"
            bgcolor="#edf1f5"
            p={isMobile ? 0.5 : 1}
            style={{ position: "sticky", top: 0, zIndex: 10 }} // fixa abaixo do header
          >
            {/* Mostra botão voltar apenas no desktop aqui — no mobile ele já está na barra superior */}
            {!isMobile && (mobilePanel === "center" || selectedChat) && (
              <IconButton
                onClick={() => {
                  setSelectedChat(null);
                  // no mobile voltamos para a lista — no desktop apenas fecha a conversa
                  if (isMobile) setMobilePanel("list");
                }}
                size="small"
                style={{ marginRight: 8 }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            {/* desktop header tabs only (mobile uses shared bar above; back arrow removed from here) */}
            {!isMobile && (
              <Tabs
                value={tab}
                onChange={(e, v) => {
                  setTab(v);
                  setSelectedChat(null);
                }}
                indicatorColor="primary"
                textColor="primary"
                centered
                variant="standard"
                scrollButtons="off"
                style={{ flex: 1 }}
              >
                <Tab
                  label={
                    <Badge
                      color="primary"
                      badgeContent={contagem.andamento}
                      overlap="rectangular"
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      style={{ pointerEvents: "none" }} // evita interceptar clicks
                    >
                      🛒 Pedido
                    </Badge>
                  }
                />

                <Tab
                  label={
                    <Badge
                      color="secondary"
                      badgeContent={contagem.producao}
                      overlap="rectangular"
                    >
                      <span style={{ color: "#1e293b" }}>🍽️ Cozinha</span>
                    </Badge>
                  }
                />

                <Tab
                  label={
                    <Badge
                      color="error"
                      badgeContent={contagem.retirada}
                      overlap="rectangular"
                    >
                      <span style={{ color: "#1e293b" }}>📍 Retirada</span>
                    </Badge>
                  }
                />

                <Tab
                  label={
                    <Badge
                      color="error"
                      badgeContent={contagem.delivery}
                      overlap="rectangular"
                    >
                      <span style={{ color: "#1e293b" }}>🚚 Delivery</span>
                    </Badge>
                  }
                />

                <Tab
                  label={
                    <Badge
                      color="default"
                      badgeContent={contagem.finalizado}
                      overlap="rectangular"
                    >
                      <span style={{ color: "#1e293b" }}>✅ Finalizados</span>
                    </Badge>
                  }
                />
              </Tabs>
            )}
          </Box>

          {/* Conteúdo */}
          {!selectedChat ? (
            <Box flex={1} p={3} style={{ overflowY: "auto" }}>
              {pedidosFiltrados.length === 0 ? (
                <Typography color="textSecondary" align="center">
                  Nenhum pedido nesta categoria.
                </Typography>
              ) : (
                pedidosFiltrados.map((pedido) => (
                  <Paper
                    key={pedido.id}
                    elevation={3}
                    style={{
                      padding: 16,
                      marginBottom: 14,
                      borderRadius: 12,
                      borderLeft: "6px solid #1976d2",
                      backgroundColor: "#fff",
                      transition: "0.25s",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      flexWrap="wrap"
                    >
                      <Box
                        flex={1}
                        onClick={() => {
                          const chat = chats.find((c) => c.id === pedido.chatId);
                          if (chat) setSelectedChat({ ...chat });
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          mb={0.5}
                          style={{ gap: 8, justifyContent: "space-between" }}
                        >
                          <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 600, textTransform: "uppercase" }}
                          >
                            {pedido.nome}
                          </Typography>

                          {/* 🚚 Badge de tipo de entrega */}
                          {pedido.resumo && (
                            <Box
                              bgcolor={
                                pedido.resumo.includes("🚚 *ENTREGA*") ||
                                pedido.resumo.includes("🏠 Endereço:")
                                  ? "#1d4ed8" // azul forte
                                  : "#b45309" // laranja forte
                              }
                              color="#ffffff"
                              px={1}
                              py={0.4}
                              borderRadius={999}
                              fontSize={11}
                              fontWeight={800}
                              display="inline-flex"
                              alignItems="center"
                              style={{
                                gap: 6,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                                textTransform: "uppercase",
                                letterSpacing: 0.6,
                              }}
                            >
                              {pedido.resumo.includes("🚚 *ENTREGA*") ||
                              pedido.resumo.includes("🏠 Endereço:")
                                ? "🚚 Entrega"
                                : "📍 Retirada"}
                            </Box>
                          )}
                        </Box>

                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ textTransform: "uppercase" }}
                        >
                          {pedido.telefone}
                        </Typography>

                        <Box
                          mt={1.5}
                          p={1.2}
                          bgcolor={getResumoBg(pedido.status)}
                          borderRadius={8}
                          border="1px solid #dbe2ef"
                          style={{
                            fontFamily: "monospace",
                            fontSize: 13,
                            whiteSpace: "pre-wrap",
                            color: "#111",
                            textTransform: "uppercase",
                            lineHeight: 1.4,
                            fontWeight: 500,
                            maxHeight: 150,
                            overflowY: "auto",
                          }}
                        >
                          {pedido.resumo ||
                            pedido.mensagem ||
                            "Sem resumo disponível"}
                        </Box>

                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ display: "block", marginTop: 6 }}
                        >
                          ⏰ {new Date(pedido.dataHora).toLocaleTimeString()}
                        </Typography>
                      </Box>

                      {/* 🔥 Esconde totalmente o botão se estiver finalizado */}
                      {pedido.status !== "finalizado" && (
                        <Box ml={2}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleMudarStatus(pedido)}
                            startIcon={
                              pedido.status === "andamento"
                                ? <span>🍳</span>
                                : pedido.status === "producao"
                                ? (isEntrega(pedido) ? <span>🚚</span> : <span>📍</span>)
                                : pedido.status === "delivery"
                                ? <span>✔️</span>
                                : null
                            }
                            style={{
                              fontWeight: 600,
                              borderRadius: 8,
                              textTransform: "uppercase",
                              backgroundColor:
                                pedido.status === "andamento"
                                  ? "#1ea3f0ff"
                                  : pedido.status === "producao"
                                    ? "#8b5cf6"
                                    : "#16a34a",
                              color: "#fff",
                              minWidth: isMobile ? 100 : 140,
                            }}
                          >
                            {pedido.status === "andamento"
                              ? "Enviar para Cozinha"
                              : pedido.status === "producao"
                                ? (isEntrega(pedido) ? "Enviar para delivery" : "Enviar para retirada")
                                : "Finalizar"}
                          </Button>
                        </Box>
                      )}

                    </Box>

                    <Divider style={{ margin: "12px 0" }} />

                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      style={{ marginTop: 8 }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleImprimir(pedido)}
                        startIcon={<span>🖨️</span>}
                        style={{
                          borderRadius: 8,
                          borderColor: "#cbd5e1",
                          textTransform: "none",
                          fontWeight: 500,
                          marginRight: 10,
                        }}
                      >
                        Imprimir
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleExcluir(pedido)}
                        startIcon={<span>🗑️</span>}
                        style={{
                          borderRadius: 8,
                          borderColor: "#f87171",
                          color: "#dc2626",
                          textTransform: "none",
                          fontWeight: 500,
                        }}
                      >
                        Excluir
                      </Button>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          ) : (
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              height="100%"
              overflow="hidden"
            >
              <ChatWindow
                key={selectedChat?.id} // 🔥 força recriar quando mudar de chat
                chat={selectedChat}
                mensagens={mensagens}
                onSend={handleSend}
              />
            </Box>
          )}
        </div>
      )}

      {/* Modal confirmDialog responsivo */}
      {confirmDialog.open && (
        <Paper
          style={{
            position: "fixed",
            top: isMobile ? "35%" : "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: 14,
            borderRadius: 12,
            zIndex: 10000,
            width: isMobile ? "90%" : 320,
          }}
          elevation={6}
        >
          <Typography variant="h6" gutterBottom>
            Mover pedido?
          </Typography>

          <Typography variant="body1" style={{ marginBottom: 16 }}>
            Confirmar mudança para o status:{" "}
            <strong>{confirmDialog.novoStatus.toUpperCase()}</strong>?
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmDialog.avisar}
                onChange={(e) =>
                  setConfirmDialog((prev) => ({
                    ...prev,
                    avisar: e.target.checked,
                  }))
                }
                color="primary"
              />
            }
            label="Avisar cliente por mensagem"
          />

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              onClick={() =>
                setConfirmDialog({
                  open: false,
                  pedido: null,
                  novoStatus: "",
                  avisar: true,
                })
              }
              style={{ marginRight: 10 }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={confirmarMudancaStatus}
            >
              Confirmar
            </Button>
          </Box>
        </Paper>
      )}
    </div>
  );
}
